// ---------------------------------------------------------------------------
// Authored by Claude (Anthropic), working in Cowork with Matt Olden,
// Context Networks. Written 2026-10-29.
// Decisions, conventions and production data corrections are Matt's;
// the code is Claude's.
// ---------------------------------------------------------------------------
import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Clock, CheckCircle, XCircle, FileText, Download } from 'lucide-react'

const OrderStatus = () => {
  const { orderId } = useParams()
  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchOrderStatus()
  }, [orderId])

  const fetchOrderStatus = async () => {
    try {
      const response = await fetch(`/api/orders/${orderId}/status`, { credentials: 'include' })
      if (response.ok) {
        const orderData = await response.json()
        setOrder(orderData)
      } else if (response.status === 401) {
        setError('Please sign in to view this order')
      } else if (response.status === 404) {
        setError('Order not found')
      } else {
        setError('Failed to fetch order status')
      }
    } catch (error) {
      console.error('Error fetching order status:', error)
      setError('Failed to fetch order status')
    } finally {
      setLoading(false)
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-5 h-5 text-warning-500" />
      case 'processing':
        return <Clock className="w-5 h-5 text-primary-500 animate-spin" />
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-success-500" />
      case 'cancelled':
        return <XCircle className="w-5 h-5 text-danger-500" />
      default:
        return <Clock className="w-5 h-5 text-gray-500" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'text-warning-600 bg-warning-50 border-warning-200'
      case 'processing':
        return 'text-primary-600 bg-primary-50 border-primary-200'
      case 'completed':
        return 'text-success-600 bg-success-50 border-success-200'
      case 'cancelled':
        return 'text-danger-600 bg-danger-50 border-danger-200'
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200'
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString()
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-center items-center h-64">
          <div className="loading-spinner"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-300">Loading order status...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="card text-center">
          <XCircle className="w-16 h-16 text-danger-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">Order Not Found</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6">{error}</p>
          <Link to="/" className="btn-primary">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Order Status</h1>
            <p className="text-gray-600 dark:text-gray-300">Order ID: {orderId}</p>
          </div>
        </div>
        
        <button
          onClick={fetchOrderStatus}
          className="btn-secondary"
        >
          Refresh
        </button>
      </div>

      {/* Status Banner */}
      <div className={`p-6 rounded-xl border-2 ${getStatusColor(order.status)}`}>
        <div className="flex items-center space-x-4">
          {getStatusIcon(order.status)}
          <div>
            <h2 className="text-xl font-semibold capitalize">{order.status}</h2>
            <p className="text-sm opacity-75">
              {order.status === 'pending' && 'Your order is waiting to be processed'}
              {order.status === 'processing' && 'Your order is currently being processed'}
              {order.status === 'completed' && 'Your order has been completed successfully'}
              {order.status === 'cancelled' && 'Your order has been cancelled'}
            </p>
          </div>
        </div>
      </div>

      {/* Order Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Campaign Information */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Campaign Details</h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Advertiser</label>
              <p className="text-gray-800 dark:text-gray-100">{order.advertiser}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Campaign Name</label>
              <p className="text-gray-800 dark:text-gray-100">{order.campaign}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Ad Type</label>
              <p className="text-gray-800 dark:text-gray-100">{order.ad_type}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Target Audience</label>
              <p className="text-gray-800 dark:text-gray-100">{order.target_audience}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Budget</label>
              <p className="text-gray-800 dark:text-gray-100">${order.budget?.toLocaleString()}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Duration</label>
              <p className="text-gray-800 dark:text-gray-100">{order.duration} days</p>
            </div>
          </div>
        </div>

        {/* Order Information */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Order Information</h3>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Order ID</label>
              <p className="text-gray-800 dark:text-gray-100 font-mono">{order.id}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Submitted</label>
              <p className="text-gray-800 dark:text-gray-100">{formatDate(order.created_at)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Last Updated</label>
              <p className="text-gray-800 dark:text-gray-100">{formatDate(order.updated_at)}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600 dark:text-gray-300">Status</label>
              <div className="flex items-center space-x-2">
                {getStatusIcon(order.status)}
                <span className="capitalize font-medium">{order.status}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Files */}
      {order.files && order.files.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Uploaded Files</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {order.files.map((file, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                <FileText className="w-5 h-5 text-gray-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {file.original_name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(file.size / 1024 / 1024).toFixed(1)} MB
                  </p>
                </div>
                <button
                  onClick={() => {
                    // Use file_url from CDN if available, otherwise fall back to file_path
                    const fileUrl = file.url || (file.filename ? `https://${window.location.hostname}/uploads/${file.filename}` : null);
                    if (fileUrl) {
                      window.open(fileUrl, '_blank');
                    } else {
                      alert('File URL not available');
                    }
                  }}
                  className="p-1 hover:bg-gray-200 rounded text-gray-600 hover:text-gray-800"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notes */}
      {order.notes && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Additional Notes</h3>
          <p className="text-gray-700 whitespace-pre-wrap">{order.notes}</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-center space-x-4">
        <Link to="/" className="btn-secondary">
          Create New Order
        </Link>
        <Link to="/admin" className="btn-primary">
          View All Orders
        </Link>
      </div>
    </div>
  )
}

export default OrderStatus